/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type { ReviewFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';

import _t from 'i18n!nls/xdp';

type Props = {
  review?: ReviewFragment | null;
  trackingIndex?: number;
  ariaHidden?: boolean;
};

const styles = {
  wrapper: () => css`
    display: flex;
    padding: var(--cds-spacing-300) var(--cds-spacing-400);
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-400);
    }
  `,
  avatar: () => css`
    height: 60px;
    width: 60px;
    border-radius: 200px;
    background: var(--cds-color-blue-900);
    display: flex;
    flex: none;
    justify-content: center;
    align-items: center;

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  name: () => css`
    margin-left: var(--cds-spacing-150);
    height: 60px;
    align-items: center;
    display: flex;
    min-width: 112px;

    ${breakpoints.down('sm')} {
      position: absolute;
      height: auto;
      margin-bottom: 4px;
      margin-left: 0;
    }
  `,
  review: () => css`
    margin-left: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-left: 0;
      margin-top: var(--cds-spacing-200);
    }
  `,
  score: () => css`
    margin-bottom: var(--cds-spacing-150);
    display: inline-flex;
    align-items: center;

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  star: () => css`
    color: var(--cds-color-blue-700);
    margin-right: var(--cds-spacing-50);
  `,
  rating: () => css`
    ::after {
      content: '·';
      padding: var(--cds-spacing-100);
      color: var(--cds-color-grey-600);
    }
  `,
  reviewDescription: () => css`
    ${typography2.bodyPrimary}
  `,
};

const ReviewCard: React.FC<Props> = ({ review, trackingIndex, ariaHidden }) => {
  const eventingProductData = useEventingProductData();
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_review_card',
    {
      reviewCard: {
        star: review?.rating ?? -1,
        index: trackingIndex ?? -1,
      },
      ...eventingProductData,
    }
  );

  if (!review) {
    return null;
  }

  return (
    <div css={styles.wrapper} ref={visibilityTrackingRef}>
      <div css={styles.avatar}>
        <Typography color="invertBody" variant="h1semibold" component="div" aria-hidden>
          {review.authorName.charAt(0)}
        </Typography>
      </div>
      <div css={styles.name}>
        <Typography2 variant="subtitleMedium" component="div">
          {review.authorName}
        </Typography2>
      </div>
      <div css={styles.review}>
        <span css={styles.score}>
          <StarFilledIcon size="small" color="interactive" css={styles.star} />
          <Typography2 variant="subtitleMedium" component="span" css={styles.rating}>
            {review.rating}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary">
            {_t('Reviewed on #{date}', {
              date: formatDateTimeDisplay(review.timestamp, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
            })}
          </Typography2>
        </span>
        <TogglableDescription
          ariaHidden={ariaHidden}
          description={review.comment.replace(/\\n/g, '\n')}
          trackingName="review_card_show_more"
          css={styles.reviewDescription}
        />
      </div>
    </div>
  );
};

export default ReviewCard;
